// src/components/shell/popovers/ActivityBannerPopover.tsx
// Bell trigger in TopStrip + the persistent "activity banner" surface. Owns
// the bell button, the popover open/close state, and the desktop anchor math.
// The actual panel body (impersonation strip + notifications + ad alerts +
// achievements) lives in ActivityBannerPanel.tsx — extracted in the round-2
// closeout slice so this file stays under the file-size-hygiene soft cap.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDevImpersonationStorage } from "@/hooks/useDevImpersonationStorage";
import { useSidebarBreakpoint } from "@/components/shell/Sidebar/useSidebarBreakpoint";
import { useWorkplaceMessagesState } from "@/components/messages/useWorkplaceMessagesState";
import { ActivityBannerBell } from "./ActivityBannerBell";
import { ActivityBannerPanel, DESKTOP_PANEL_WIDTH } from "./ActivityBannerPanel";

export type ActivityBannerPopoverProps = {
  allowTestNotification?: boolean;
  allowSenderLock?: boolean;
  allowRequiresAck?: boolean;
  /**
   * Called when the user clicks the ad-alerts row inside the banner. The
   * parent (LeadWorkspaceShell) wires this to set the Ads ingestion intent
   * + switch the active mode to adsAssistant. Optional — when omitted the
   * row renders read-only.
   */
  onOpenAdsIngestion?: () => void;
};

const PANEL_OFFSET_PX = 8;

export function ActivityBannerPopover({
  allowTestNotification = false,
  allowSenderLock = false,
  allowRequiresAck = false,
  onOpenAdsIngestion,
}: ActivityBannerPopoverProps) {
  const state = useWorkplaceMessagesState();
  const variant = useSidebarBreakpoint();
  const impersonation = useDevImpersonationStorage();

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Fire loadOnOpen exactly when `open` transitions to true. Keying on the
  // whole `state` object would re-fire on every render (object identity
  // changes), which is what was causing the bell to flash and re-fetch the
  // messages inbox repeatedly -- see useWorkplaceMessagesState `loadList`.
  const loadOnOpen = state.loadOnOpen;
  useEffect(() => {
    if (!open) return;
    loadOnOpen();
  }, [open, loadOnOpen]);

  useEffect(() => {
    if (!open || variant === "mobile") return;
    const update = () => {
      const node = triggerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const left = Math.max(
        12,
        Math.min(viewportWidth - DESKTOP_PANEL_WIDTH - 12, rect.right - DESKTOP_PANEL_WIDTH)
      );
      setPosition({ left, top: rect.bottom + PANEL_OFFSET_PX });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, variant]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (state.capable === false) return null;

  return (
    <>
      <ActivityBannerBell
        ref={triggerRef}
        open={open}
        unreadCount={state.unreadCount}
        unackedCount={state.unackedCount}
        reduceMotion={state.reduceMotion}
        onClick={() => setOpen((o) => !o)}
      />
      {open
        ? createPortal(
            <ActivityBannerPanel
              isMobile={variant === "mobile"}
              panelRef={panelRef}
              position={position}
              impersonation={impersonation}
              wpState={state}
              allowTestNotification={allowTestNotification}
              allowSenderLock={allowSenderLock}
              allowRequiresAck={allowRequiresAck}
              open={open}
              onClose={() => setOpen(false)}
              onOpenAdsIngestion={onOpenAdsIngestion}
            />,
            document.body
          )
        : null}
    </>
  );
}
